import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { CognitoUserPool } from 'amazon-cognito-identity-js';
import { ChartConfiguration, ChartData, ChartType } from 'chart.js';
import { BaseChartDirective, ThemeService } from 'ng2-charts';
import { environment } from 'src/environments/environment';
import { FabButton } from '../nav/fab-menu/fab-menu.component';
import { AccountResult, AnalyticsResultsService, DailyBalanceStatisticAll, IncomeVsSpendStatistic, TransactionCategoryGroupStatistic, TransactionCategoryStatistic } from '../shared/services/analytics-results.service';
import { Asset, AssetsService } from '../shared/services/assets.service';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit {
  constructor(private router: Router, private resultsAnalyticsSvc: AnalyticsResultsService, private breakpointObserver: BreakpointObserver) {
    breakpointObserver.observe([
      Breakpoints.Handset
    ]).subscribe(result => {
      if (result.matches) {
        this.isMobile = true;
      } else {
        this.isMobile = false;
      }

      if (this.currentYearTxnCatGroupsLoaded) {
        this.buildCatGroupStats();
      }
    });

  }

  currentYearTxnCatGroups: TransactionCategoryGroupStatistic[] = new Array<TransactionCategoryGroupStatistic>();
  statsNotFound: boolean = true;
  statsReady: boolean = false;
  currentYearTxnCatGroupsLoaded: boolean = false;
  isMobile: boolean = false;
  latestYearMonth = 0;
  latestYearMonthName = "";
  previousYearMonth = 0;
  previousYearMonthName = "";
  minTxnCurrent = Number.MAX_SAFE_INTEGER;
  minTxnPrevious = Number.MAX_SAFE_INTEGER;
  minTxnState = "equal";
  minPercentDelta = 0;
  maxTxnCurrent = 0;
  maxTxnPrevious = 0;
  maxTxnState = "equal";
  maxPercentDelta = 0;
  avgTxnCurrent = 0;
  avgTxnPrevious = 0;
  avgTxnState = "equal";
  avgPercentDelta = 0;
  sumTxnCurrent = 0;
  sumTxnPrevious = 0;
  sumTxnState = "equal";
  sumPercentDelta = 0;
  countTxnCurrent = 0;
  countTxnPrevious = 0;
  countTxnState = "equal";
  countPercentDelta = 0;

  fabButtons: FabButton[] = [
    {
      icon: 'savings',
      label: 'New Asset',
      route: '/assets/create'
    },
    {
      icon: 'payments',
      label: 'New Debt',
      route: '/debts/create'
    },
    {
      icon: 'real_estate_agent',
      label: 'New Purchase',
      route: '/guides/purchase/new'
    }
  ];


  ngOnInit(): void {
    this.resultsAnalyticsSvc.getCategoryGroupsForTransactionsCurrentYear()
      .subscribe({
        next: (data) => {
          this.currentYearTxnCatGroups = data;
          this.buildCatGroupStats();
          this.currentYearTxnCatGroupsLoaded = true;
        },
        error: (err: HttpErrorResponse) => {
          if (err.status == 400) {
            this.currentYearTxnCatGroupsLoaded = false;
          } else {
            throw err;
          }
        }
      });
  }

  buildCatGroupStats() {
    if (this.currentYearTxnCatGroups && this.currentYearTxnCatGroups.length > 0) {

      this.minPercentDelta = 0;
      this.maxPercentDelta = 0;
      this.sumPercentDelta = 0;
      this.avgPercentDelta = 0;
      this.countPercentDelta = 0;      

      this.minTxnCurrent = 0;
      this.minTxnPrevious = 0;
      this.maxTxnCurrent = 0;
      this.maxTxnPrevious = 0;
      this.sumTxnCurrent = 0;
      this.sumTxnPrevious = 0;
      this.avgTxnCurrent = 0;
      this.avgTxnPrevious = 0;
      this.countTxnCurrent = 0;
      this.countTxnPrevious = 0;


      this.currentYearTxnCatGroups.forEach((x) => {
        if (x.dim_txndate_yearmonth as number > this.latestYearMonth) {
          this.previousYearMonth = this.latestYearMonth;
          this.previousYearMonthName = this.latestYearMonthName;
          this.latestYearMonth = x.dim_txndate_yearmonth as number;
          this.latestYearMonthName = x.dim_txndate_yearmonthname;
        }
      });

      this.currentYearTxnCatGroups.forEach((x) => {
        if (x.dim_txncatgrp !== "No Category Group") {
          if (x.dim_txndate_yearmonth === this.latestYearMonth) {
            this.countTxnCurrent = this.countTxnCurrent + (x.fct_count_abs_amount as number);

            this.sumTxnCurrent = this.sumTxnCurrent + (x.fct_sum_abs_amount as number);
            this.avgTxnCurrent = this.sumTxnCurrent / this.countTxnCurrent;

            if (x.fct_max_abs_amount as number > this.maxTxnCurrent) {
              this.maxTxnCurrent = x.fct_max_abs_amount as number;
            }

            if (x.fct_min_abs_amount as number < this.minTxnCurrent) {
              this.minTxnCurrent = x.fct_min_abs_amount as number;
            }
          }

          if (x.dim_txndate_yearmonth === this.previousYearMonth) {
            this.countTxnPrevious = this.countTxnPrevious + (x.fct_count_abs_amount as number);

            this.sumTxnPrevious = this.sumTxnPrevious + (x.fct_sum_abs_amount as number);
            this.avgTxnPrevious = this.sumTxnPrevious / this.countTxnPrevious;

            if (x.fct_max_abs_amount as number > this.maxTxnPrevious) {
              this.maxTxnPrevious = x.fct_max_abs_amount as number;
            }

            if (x.fct_min_abs_amount as number < this.minTxnPrevious) {
              this.minTxnPrevious = x.fct_min_abs_amount as number;
            }
          }
        }
      });

      this.minTxnState = "equal";
      this.maxTxnState = "equal";
      this.sumTxnState = "equal";
      this.avgTxnState = "equal";
      this.countTxnState = "equal";

      this.minTxnState = this.minTxnCurrent > this.minTxnPrevious ? "increasing" : this.minTxnState;
      this.minTxnState = this.minTxnCurrent < this.minTxnPrevious ? "decreasing" : this.minTxnState;

      this.maxTxnState = this.maxTxnCurrent > this.maxTxnPrevious ? "increasing" : this.maxTxnState;
      this.maxTxnState = this.maxTxnCurrent < this.maxTxnPrevious ? "decreasing" : this.maxTxnState;

      this.sumTxnState = this.sumTxnCurrent > this.sumTxnPrevious ? "increasing" : this.sumTxnState;
      this.sumTxnState = this.sumTxnCurrent < this.sumTxnPrevious ? "decreasing" : this.sumTxnState;

      this.avgTxnState = this.avgTxnCurrent > this.avgTxnPrevious ? "increasing" : this.avgTxnState;
      this.avgTxnState = this.avgTxnCurrent < this.avgTxnPrevious ? "decreasing" : this.avgTxnState;

      this.countTxnState = this.countTxnCurrent > this.countTxnPrevious ? "increasing" : this.countTxnState;
      this.countTxnState = this.countTxnCurrent < this.countTxnPrevious ? "decreasing" : this.countTxnState;

      this.minPercentDelta = (this.minTxnCurrent - this.minTxnPrevious) / this.minTxnPrevious;
      this.maxPercentDelta = (this.maxTxnCurrent - this.maxTxnPrevious) / this.maxTxnPrevious;
      this.sumPercentDelta = (this.sumTxnCurrent - this.sumTxnPrevious) / this.sumTxnPrevious;
      this.avgPercentDelta = (this.avgTxnCurrent - this.avgTxnPrevious) / this.avgTxnPrevious;
      this.countPercentDelta = (this.countTxnCurrent - this.countTxnPrevious) / this.countTxnPrevious;

      this.statsReady = true;
    }
  }

  getQuater(at: Date): number {
    let mon = at.getMonth() + 1; //get month is zero based

    if(mon >= 1 && mon <= 3){
      return 1;
    }

    if(mon >= 4 && mon <= 6){
      return 2;
    }

    if(mon >= 7 && mon <= 9){
      return 3;
    }

    return 4;
  }

  addMonths(at: Date, months: number): Date {
    let d = new Date(at);
    d.setMonth(d.getMonth() + months);

    return d;
  }

  addQuaters(at: Date, quaters: number): Date {
    return this.addMonths(at, quaters * 3);
  }

  getMonthYear(at: Date): number {
    let monthYear = at.getFullYear() * 100;
    monthYear = monthYear + at.getMonth() + 1; //get month is zero based
    return monthYear;
  }

  getQuaterYear(at: Date): number {
    let quaterYear = at.getFullYear() * 100;
    quaterYear = quaterYear + this.getQuater(at);
    return quaterYear;
  }

  getIcon(state: string) {
    if (state === "increasing")
      return "trending_up";

    if (state === "decreasing")
      return "trending_down";

    return "trending_flat";
  }

  getIconColourUpIsGood(state: string) {
    if (state === "increasing")
      return "trending-good";

    if (state === "decreasing")
      return "trending-bad";

    return "";
  }

  getIconColourDownIsGood(state: string) {
    if (state === "increasing")
      return "trending-bad";

    if (state === "decreasing")
      return "trending-good";

    return "";
  }

}

// export class IncomeVsSpendTableRow {
//   public 
// }